import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { Box, Stack, Typography, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { IllustrationFiles } from './illustrations/IllustrationFiles';
import { getFileIconLarge } from 'utils/file-icon';
import { limitItems } from 'utils/array';
import { ExpandableText } from 'components/common/ExpandableText';

export const EntityFileDetails = (props) => {
  const { children } = props;

  return (
    <Stack
      direction="column"
      spacing={3}
      sx={{ height: '100%', width: '100%' }}
    >
      {children}
    </Stack>
  );
};

EntityFileDetails.propTypes = {
  children: PropTypes.node,
};

export const FileType = ({ file, fileIcon = null }) => {
  return (
    <Stack
      direction="column"
      alignItems="center"
      sx={{ py: 1 }}
    >
      {fileIcon ?? getFileIconLarge(file)}
    </Stack>
  );
};

FileType.propTypes = {
  file: PropTypes.object,
  fileIcon: PropTypes.object,
};

export const PeopleWithAccess = ({ users }) => {
  const userNames = useMemo(() => users.map((user) => user.owner_account.name), [users]);

  const { visible, hidden, summary, tooltipItems } = useMemo(() => limitItems(userNames, 5), [userNames]);

  return (
    users.length > 0 && (
      <Stack
        direction="column"
        spacing={1}
      >
        <Typography
          component="h1"
          variant="subtitle2"
          sx={{ fontWeight: 500 }}
        >
          Who has access
        </Typography>
        <Typography
          component="div"
          variant="caption"
          sx={{ wordWrap: 'break-word' }}
        >
          {visible.join(', ')}
          {hidden.length > 0 && (
            <Tooltip title={tooltipItems.join(', ')}>
              <Typography
                component="p"
                variant="caption"
                sx={{ display: 'inline', cursor: 'pointer', color: 'primary.main', pl: 1 }}
              >
                {summary}
              </Typography>
            </Tooltip>
          )}
        </Typography>
      </Stack>
    )
  );
};

PeopleWithAccess.propTypes = {
  users: PropTypes.array,
};

export const Details = (props) => {
  const { title = 'Details', children } = props;

  return (
    <Stack
      direction="column"
      spacing={1}
    >
      <Typography
        component="h1"
        variant="subtitle2"
        sx={{
          fontWeight: 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%',
        }}
      >
        {title}
      </Typography>
      {children}
    </Stack>
  );
};

Details.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
};

export const DetailsItem = (props) => {
  const { title, subtitle, showMoreText, tooltipTitle } = props;

  return (
    <Stack direction="column">
      <Typography
        component="h3"
        variant="caption"
        sx={{ fontWeight: 600 }}
      >
        {title}
      </Typography>
      <Typography
        component="h2"
        variant="subtitle2"
        sx={{
          fontWeight: 400,
          minWidth: '300px',
          width: '300px',
          maxWidth: '300px',
        }}
      >
        {subtitle}
        {showMoreText && (
          <Tooltip
            title={tooltipTitle}
            arrow
            placement="bottom"
          >
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                marginLeft: 0.5,
                color: 'primary.main',
                cursor: 'pointer',
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              {showMoreText}
            </Box>
          </Tooltip>
        )}
      </Typography>
    </Stack>
  );
};

DetailsItem.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
};

export const DetailExpandableText = (props) => {
  const { title, text } = props;

  return (
    <Stack direction="column">
      <Typography
        component="h3"
        variant="caption"
        sx={{ fontWeight: 600 }}
      >
        {title}
      </Typography>
      <ExpandableText
        text={text}
        maxLength={230}
      />
    </Stack>
  );
};

DetailExpandableText.propTypes = {
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
};

export const ActionButton = (props) => {
  const { title = '', action = () => {}, icon = '', disabled = false, disabledTooltip = '' } = props;

  const content = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="right"
      spacing={1}
      sx={{
        color: disabled ? 'grey.500' : 'secondary.darker',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onClick={disabled ? null : action}
    >
      {icon}
      <Typography
        component="a"
        variant="subtitle2"
        sx={{ fontWeight: 500 }}
      >
        {title}
      </Typography>
    </Stack>
  );

  if (disabled && disabledTooltip) {
    return (
      <Tooltip
        title={disabledTooltip}
        placement="bottom-end"
      >
        <span>{content}</span>
      </Tooltip>
    );
  }

  return content;
};

ActionButton.propTypes = {
  title: PropTypes.string,
  action: PropTypes.func,
  icon: PropTypes.node,
};

export const EditButton = (props) => {
  const { title = 'Edit Artwork', action = () => {} } = props;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="right"
      spacing={1}
      sx={{ color: 'secondary.darker', cursor: 'pointer' }}
      onClick={action}
    >
      <EditOutlinedIcon sx={{ fontSize: '22px' }} />
      <Typography
        component="a"
        variant="subtitle2"
        sx={{ fontWeight: 500 }}
      >
        {title}
      </Typography>
    </Stack>
  );
};

EditButton.propTypes = {
  title: PropTypes.string,
  action: PropTypes.func,
};

export const EmptyFile = (props) => {
  const { title = 'Select a file to view details', illustration = null } = props;

  return (
    <Stack
      direction="column"
      spacing={4}
      alignItems="center"
      justifyContent="center"
      sx={{ height: '100%' }}
    >
      {illustration ?? (
        <IllustrationFiles
          width={170}
          height={150}
        />
      )}
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 400 }}
      >
        {title}
      </Typography>
    </Stack>
  );
};

EmptyFile.propTypes = {
  title: PropTypes.string,
  illustration: PropTypes.node,
};

EntityFileDetails.FileType = FileType;
EntityFileDetails.PeopleWithAccess = PeopleWithAccess;
EntityFileDetails.Details = Details;
EntityFileDetails.DetailsItem = DetailsItem;
EntityFileDetails.DetailExpandableText = DetailExpandableText;
EntityFileDetails.EditButton = EditButton;
EntityFileDetails.ActionButton = ActionButton;
EntityFileDetails.EmptyFile = EmptyFile;
